import formatDate from '@/utils/formatDate';

import { ReportLocationEnum } from '@/types/alert';
import { ReportCategoryType, ReportColor } from '@/types/transmissionReport';

const TRANSMISSION_NOTE_LIMIT = 2000;

export type LinkedTaskReportSource = {
  dateOfService: string | null;
  alertType: string | null;
  appointmentType: string | null;
  alertNotes?: string | null;
};

export type LinkedReportSnapshot = {
  billingDate?: string | null;
  color?: string | null;
  reportType?: string | null;
  notes?: Array<{ location: string; note: string }> | null;
};

export type LinkedTaskReportChanges = {
  color?: ReportColor;
  reportType?: ReportCategoryType;
  billingDate?: string | null;
  transmissionNote?: string;
};

const ALERT_COLOR: Record<string, ReportColor> = {
  red: ReportColor.Red,
  yellow: ReportColor.Yellow,
  none: ReportColor.Green
};

const ALERT_REPORT_TYPE: Record<string, ReportCategoryType> = {
  eri_tracking: ReportCategoryType.ERI_Tracking,
  physician_request: ReportCategoryType.Physician_Requested
};

function calendarDay(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) return '';
  return formatDate(trimmed);
}

function transmissionNote(report: LinkedReportSnapshot): string {
  return (
    report.notes?.find((note) => note.location === ReportLocationEnum.Transmission)?.note.trim() ??
    ''
  );
}

/**
 * Fields to copy from a linked task onto the open report.
 * Remote / In Clinic is left alone. That save deletes episode cards.
 * Returns null when the report already matches the task.
 */
export function reportFieldsFromLinkedTask(
  task: LinkedTaskReportSource,
  report: LinkedReportSnapshot
): LinkedTaskReportChanges | null {
  const changes: LinkedTaskReportChanges = {};

  const alertType = task.alertType?.trim() ?? '';
  const color = ALERT_COLOR[alertType];
  if (color && color !== report.color) changes.color = color;

  const reportType = ALERT_REPORT_TYPE[alertType];
  if (reportType && reportType !== report.reportType) changes.reportType = reportType;

  const taskDateRaw = task.dateOfService?.trim() ?? '';
  const reportDay = calendarDay(report.billingDate);
  if (!taskDateRaw) {
    if (reportDay) changes.billingDate = null;
  } else {
    const taskDay = calendarDay(taskDateRaw);
    if (taskDay && taskDay !== reportDay) changes.billingDate = taskDay;
  }

  const taskNote = task.alertNotes?.trim() ?? '';
  if (taskNote) {
    const note = taskNote.slice(0, TRANSMISSION_NOTE_LIMIT);
    if (note !== transmissionNote(report)) changes.transmissionNote = note;
  }

  return Object.keys(changes).length ? changes : null;
}
